import React, { useEffect, useMemo, useState } from "react";
import { Download, Loader2, Lock, Unlock, RefreshCw, Users, ListOrdered } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, formatApiError, photoUrl } from "../lib/api";
import { toast } from "sonner";

export default function AdminVoting() {
    const [results, setResults] = useState([]);
    const [totalVotes, setTotalVotes] = useState(0);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [endsAt, setEndsAt] = useState("");
    const [view, setView] = useState("flat"); // 'flat' | 'by_manager'

    const fetchAll = async (silent = false) => {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        try {
            const [r1, r2] = await Promise.all([api.get("/votes/results"), api.get("/settings")]);
            setResults(r1.data.results);
            setTotalVotes(r1.data.total_votes);
            setSettings(r2.data);
            if (r2.data.voting_ends_at) {
                const d = new Date(r2.data.voting_ends_at);
                const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                setEndsAt(local);
            }
        } catch (e) {
            toast.error(formatApiError(e, "Failed to load voting data"));
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchAll();
        const t = setInterval(() => fetchAll(true), 8000);
        return () => clearInterval(t);
    }, []);

    const toggleVoting = async () => {
        try {
            const next = !(settings?.voting_open_flag);
            const { data } = await api.patch("/settings", { voting_open: next });
            setSettings(data);
            toast.success(next ? "Voting is now open" : "Voting closed");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const setEndTime = async () => {
        if (!endsAt) return;
        try {
            const iso = new Date(endsAt).toISOString();
            const { data } = await api.patch("/settings", { voting_ends_at: iso });
            setSettings(data);
            toast.success("Voting deadline updated");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const exportCsv = async () => {
        try {
            const res = await api.get("/votes/export", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "tmc_voting_results.csv";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const winner = results[0];
    const maxCount = Math.max(1, ...results.map((r) => r.vote_count));

    // Group results by manager — sums team votes, sorts entries within team
    const byManager = useMemo(() => {
        const groups = new Map();
        for (const r of results) {
            const key = (r.manager && r.manager.trim()) || "— Unassigned —";
            if (!groups.has(key)) groups.set(key, { manager: key, total: 0, entries: [] });
            const g = groups.get(key);
            g.total += r.vote_count || 0;
            g.entries.push(r);
        }
        const arr = Array.from(groups.values());
        arr.forEach((g) => g.entries.sort((a, b) => (b.vote_count || 0) - (a.vote_count || 0)));
        arr.sort((a, b) => b.total - a.total);
        return arr;
    }, [results]);
    const managerMaxTotal = Math.max(1, ...byManager.map((g) => g.total));

    return (
        <div className="App min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1">
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-voting">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                        <div>
                            <div className="label-overline">Admin · Voting</div>
                            <h1 className="font-serif text-4xl text-tmc-ink mt-3">Live Results</h1>
                            <p className="text-tmc-muted mt-2">
                                {totalVotes} total {totalVotes === 1 ? "vote" : "votes"} · refreshing every 8s.
                            </p>
                        </div>
                        <div className="flex gap-3 items-center">
                            {refreshing && <RefreshCw className="w-4 h-4 text-tmc-muted animate-spin" />}
                            <button onClick={() => fetchAll()} className="tmc-btn-secondary text-sm py-2 px-4" data-testid="refresh-btn">
                                Refresh
                            </button>
                            <button onClick={exportCsv} className="tmc-btn-primary text-sm py-2 px-4" data-testid="export-csv-btn">
                                <Download className="w-4 h-4" /> Export CSV
                            </button>
                        </div>
                    </div>

                    {/* Controls */}
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="voting-controls">
                        <div className="tmc-card p-6">
                            <div className="label-overline">Voting window</div>
                            <div className="mt-3 flex items-center justify-between">
                                <div>
                                    <div className="font-serif text-2xl text-tmc-ink">
                                        {settings?.voting_open ? "Open for votes" : "Closed"}
                                    </div>
                                    <div className="text-sm text-tmc-muted mt-1">
                                        {settings?.voting_open_flag ? "Flag: ON" : "Flag: OFF"}
                                        {settings?.voting_ends_at && (
                                            <> · ends {new Date(settings.voting_ends_at).toLocaleString()}</>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={toggleVoting}
                                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium ${
                                        settings?.voting_open_flag
                                            ? "bg-tmc-ink text-white hover:opacity-90"
                                            : "bg-tmc-moss text-white hover:bg-tmc-mossDark"
                                    }`}
                                    data-testid="toggle-voting-btn"
                                >
                                    {settings?.voting_open_flag ? (
                                        <>
                                            <Lock className="w-4 h-4" /> Close voting
                                        </>
                                    ) : (
                                        <>
                                            <Unlock className="w-4 h-4" /> Open voting
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="tmc-card p-6">
                            <div className="label-overline">Set deadline</div>
                            <div className="mt-3 flex flex-col sm:flex-row gap-3 sm:items-end">
                                <div className="flex-1">
                                    <label className="block text-xs text-tmc-muted mb-1">Voting ends at</label>
                                    <input
                                        type="datetime-local"
                                        value={endsAt}
                                        onChange={(e) => setEndsAt(e.target.value)}
                                        className="tmc-input"
                                        data-testid="ends-at-input"
                                    />
                                </div>
                                <button onClick={setEndTime} className="tmc-btn-secondary text-sm py-2.5 px-5" data-testid="set-deadline-btn">
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Leaderboard */}
                    <div className="mt-10" data-testid="leaderboard">
                        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                            <h2 className="font-serif text-2xl text-tmc-ink">Leaderboard</h2>
                            <div className="inline-flex rounded-full border border-tmc-line bg-white p-1 self-start" data-testid="leaderboard-tabs">
                                <button
                                    onClick={() => setView("flat")}
                                    className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                        view === "flat" ? "bg-tmc-ink text-white" : "text-tmc-inkSoft hover:text-tmc-terracotta"
                                    }`}
                                    data-testid="tab-flat"
                                >
                                    <ListOrdered className="w-4 h-4" /> All finalists
                                </button>
                                <button
                                    onClick={() => setView("by_manager")}
                                    className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                        view === "by_manager" ? "bg-tmc-ink text-white" : "text-tmc-inkSoft hover:text-tmc-terracotta"
                                    }`}
                                    data-testid="tab-by-manager"
                                >
                                    <Users className="w-4 h-4" /> By manager
                                </button>
                            </div>
                        </div>

                        {loading ? (
                            <div className="py-16 flex items-center justify-center text-tmc-muted">
                                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                            </div>
                        ) : results.length === 0 ? (
                            <div className="py-16 text-center text-tmc-muted">
                                No finalists yet. Mark submissions as Finalists to populate the leaderboard.
                            </div>
                        ) : view === "flat" ? (
                            <div className="mt-6 space-y-3">
                                {results.map((r, i) => (
                                    <div
                                        key={r.id}
                                        className="tmc-card p-4 sm:p-5 flex items-center gap-4"
                                        data-testid={`leaderboard-row-${i}`}
                                    >
                                        <div className="font-serif italic text-2xl text-tmc-terracotta w-10 text-center">
                                            {i + 1}
                                        </div>
                                        {r.photo_url && (
                                            <img
                                                src={photoUrl(r.photo_url)}
                                                alt=""
                                                className="w-16 h-16 object-cover rounded-lg border border-tmc-line"
                                            />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-tmc-ink truncate">
                                                {r.name} <span className="text-tmc-muted text-sm font-normal">· {r.department}</span>
                                            </div>
                                            <div className="text-xs text-tmc-muted truncate mt-0.5">
                                                {r.manager ? `Manager: ${r.manager}` : "No manager"}
                                            </div>
                                            <div className="text-sm text-tmc-inkSoft line-clamp-1 mt-0.5">{r.description}</div>
                                            <div className="mt-2 h-1.5 rounded-full bg-tmc-sand overflow-hidden">
                                                <div
                                                    className="h-full bg-tmc-terracotta transition-all duration-500"
                                                    style={{ width: `${(r.vote_count / maxCount) * 100}%` }}
                                                />
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-serif text-3xl text-tmc-ink">{r.vote_count}</div>
                                            <div className="text-xs label-overline">{r.vote_count === 1 ? "vote" : "votes"}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            // BY MANAGER VIEW
                            <div className="mt-6 space-y-5" data-testid="leaderboard-by-manager">
                                {byManager.map((g, gi) => (
                                    <div key={g.manager} className="tmc-card overflow-hidden" data-testid={`mgr-group-${gi}`}>
                                        <div className="px-5 py-4 sm:px-6 sm:py-5 flex items-center gap-4 bg-tmc-sand/40 border-b border-tmc-line">
                                            <div className="font-serif italic text-2xl text-tmc-terracotta w-10 text-center">
                                                {gi + 1}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-serif text-xl text-tmc-ink truncate">
                                                    {g.manager}
                                                </div>
                                                <div className="text-xs text-tmc-muted mt-0.5">
                                                    {g.entries.length} finalist{g.entries.length === 1 ? "" : "s"} · team
                                                </div>
                                                <div className="mt-2 h-2 rounded-full bg-white overflow-hidden border border-tmc-line">
                                                    <div
                                                        className="h-full bg-gradient-to-r from-tmc-terracotta via-[#FF7A59] to-tmc-moss transition-all duration-500"
                                                        style={{ width: `${(g.total / managerMaxTotal) * 100}%` }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-serif text-3xl text-tmc-ink" data-testid={`mgr-total-${gi}`}>{g.total}</div>
                                                <div className="text-xs label-overline">team {g.total === 1 ? "vote" : "votes"}</div>
                                            </div>
                                        </div>
                                        <div className="divide-y divide-tmc-line">
                                            {g.entries.map((r, ri) => (
                                                <div
                                                    key={r.id}
                                                    className="px-5 py-3 sm:px-6 sm:py-4 flex items-center gap-4"
                                                    data-testid={`mgr-${gi}-entry-${ri}`}
                                                >
                                                    {r.photo_url && (
                                                        <img
                                                            src={photoUrl(r.photo_url)}
                                                            alt=""
                                                            className="w-12 h-12 object-cover rounded-lg border border-tmc-line"
                                                        />
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-tmc-ink truncate">
                                                            {r.name} <span className="text-tmc-muted text-sm font-normal">· {r.department}</span>
                                                        </div>
                                                        <div className="text-sm text-tmc-inkSoft line-clamp-1 mt-0.5">
                                                            {r.description}
                                                        </div>
                                                    </div>
                                                    <div className="text-right shrink-0">
                                                        <div className="font-serif text-2xl text-tmc-ink">{r.vote_count}</div>
                                                        <div className="text-[10px] label-overline tracking-[0.2em]">
                                                            {r.vote_count === 1 ? "vote" : "votes"}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {winner && winner.vote_count > 0 && (
                        <div className="mt-12 tmc-card p-8 bg-gradient-to-br from-tmc-sand to-white" data-testid="winner-card">
                            <div className="label-overline">Currently leading</div>
                            <div className="font-serif text-3xl text-tmc-ink mt-2">{winner.name} — {winner.department}</div>
                            <p className="text-tmc-inkSoft mt-2">{winner.description}</p>
                        </div>
                    )}
                </section>
            </main>
            <Footer />
        </div>
    );
}
